import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import { PreCertTable } from "../../Shared/Components/PreCertTable";
import { deepCopy } from "../../Shared/Utility/DeepCopy";
import { getDateWithOffset } from "../../Shared/Utility/GetDateWithOffset";
import { DocumentReviewStatus, PostGraduateYear } from "../Models/PostGraduateYear";
import { TrainingProgram, TrainingProgramType, TrainingProgramTypes, UnfoundProgramDefaultCode } from "../../Shared/Models/TrainingProgram";
import { LookupItem } from "../../Shared/Models/LookupItem";
import { abpnClient } from "../../Services/AbpnClient";
import { AuthContext } from "../../Auth/AuthProvider";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Alert, Button, Form, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { ValidatorHelper } from "../../Shared/Utility/ValidatorHelper";
import { ProfileContext } from "../../Auth/ProfileProvider";
import { ClinicalSkill } from "../Models/ClinicalSkill";
import { TrainingDocument } from "../Models/TrainingDocument";
import { SourceType } from "../Models/SourceType";
import { Row } from "react-table";
import { ActiveRosterContext } from "../../Contexts/ActiveRosterProvider";

interface IProps {
    physicianId: number;
    isReadonly: boolean;
    isOnFastTrack: boolean;
    postGraduateYears: Array<PostGraduateYear>;
    programTypeIdsWithApprovedApplication: Array<number>;
    pgyLookupList: Array<LookupItem>;
    saveResident: (postGraduateYears: Array<PostGraduateYear>, clinicalSkills: Array<ClinicalSkill>, isOnFastTrack: boolean) => void;
    saveTrainingDocuments: (trainingDocuments: Array<TrainingDocument>, pgy: PostGraduateYear) => void;
    isAlumni: boolean;
    setIsEditing: (value: boolean) => void;
}

export function PostGraduateYearGrid(props: IProps) {
    const getAvailableYears = (postGraduateYears: Array<PostGraduateYear>) => {
        let years = postGraduateYears.map(_ => _.sequenceNumber);
        let maxYear = years.length ? Math.max(...years) + 1 : 1;
        return props.pgyLookupList.filter(_ => _.id <= maxYear);
    }

    const [postGraduateYears, setPostGraduateYears] = useState<Array<PostGraduateYear>>(props.postGraduateYears);
    const [originalPostGraduateYears, setOriginalPostGraduateYears] = useState<Array<PostGraduateYear>>(props.postGraduateYears);
    const [programList, setProgramList] = useState<Array<TrainingProgram>>([]);
    const [isConfirmingDeletePgy, setIsConfirmingDeletePgy] = useState<boolean>(false);
    const [isConfirmingDeleteDocument, setIsConfirmingDeleteDocument] = useState<boolean>(false);
    const [documentToDelete, setDocumentToDelete] = useState<TrainingDocument>();
    const [pgyToDelete, setPgyToDelete] = useState<PostGraduateYear>();
    const [pgySelectYearLookupList, setPgySelectYearLookupList] = useState<Array<LookupItem>>(getAvailableYears(props.postGraduateYears));
    const [canSelectOtherProgramForPgyEdit, setCanSelectOtherProgramForPgyEdit] = useState<boolean>(false);
    const [userTrainingProgramType, setUserTrainingProgramType] = useState<TrainingProgramType>(null);
    const [isAttestationCheckboxDisabled, setIsAttestationCheckboxDisabled] = useState<boolean>(false);
    const [showInvalidUploadAlert, setShowInvalidUploadAlert] = useState<boolean>(false);
    const [attestationValidationErrors, setAttestationValidationErrors] = useState<Array<string>>([]);
    const [showInvalidAttestationAlert, setShowInvalidAttestationAlert] = useState<boolean>(false);
    const { reloadRoster } = useContext(ActiveRosterContext) ?? {};

    const authContext = useContext(AuthContext);
    const today = new Date();
    const currentDate = new Date();
    const maxPgyStartDate = new Date(currentDate.setMonth(currentDate.getMonth() + 3));
    const pgyEndDateFormId: string = "postGraduateYearFormEndDate";
    const pgyStartDateFormId: string = "postGraduateYearFormStartDate";
    const programCodeId: string = "programCode"
    const trainingDocumentsId: string = "trainingDocumentsColumnDisplay";
    const trainingDocumentsErrorId: string = "trainingDocumentsErrorMessage";
    const editForm = useRef(null);
    const pgyMonthsRef = useRef<HTMLInputElement>(null);
    const { user } = useContext(ProfileContext);
    const isPsychiatryProgramUser = userTrainingProgramType ? (userTrainingProgramType.trainingProgramTypeId === TrainingProgramTypes.Cap || userTrainingProgramType.trainingProgramTypeId === TrainingProgramTypes.Psychiatry) : false;
    let userProgramTypeId = user.trainingPrograms.find(_ => _.trainingProgramId === user.selectedTrainingProgramId).trainingProgramTypeId;

    useEffect(() => {
        setOriginalPostGraduateYears(deepCopy(props.postGraduateYears));
        setPostGraduateYears(deepCopy(props.postGraduateYears));
        getTrainingProgramType();
    }, [props.postGraduateYears]);

    const getTrainingProgramType = async () => {
        const trainingPrograms = await getTrainingPrograms();
        let userProgram = trainingPrograms.find(_ => _.trainingProgramId === user.selectedTrainingProgramId);
        let programType = await abpnClient.get<TrainingProgramType>(`/api/trainingProgram/getTrainingProgramType?trainingProgramTypeId=${userProgram.trainingProgramTypeId}`, authContext);
        setUserTrainingProgramType(programType);
        return programType;
    }

    const getTrainingPrograms = async () => {
        let programs = await abpnClient.get<Array<TrainingProgram>>("/api/trainingProgram/getTrainingPrograms", authContext);
        setProgramList(programs);
        return programs;
    }

    const canSelectOtherProgram = (pgy: PostGraduateYear, userProgramType: TrainingProgramType): boolean => {
        let userProgram = programList.find(_ => _.trainingProgramId === user.selectedTrainingProgramId);

        if (userProgram.trainingProgramTypeId === TrainingProgramTypes.ChN) return true;

        if ((userProgram.trainingProgramTypeId === TrainingProgramTypes.Neurology ||
            userProgram.trainingProgramTypeId === TrainingProgramTypes.Psychiatry ||
            userProgram.trainingProgramTypeId === TrainingProgramTypes.Cap ||
            userProgram.trainingProgramTypeId === TrainingProgramTypes.PsychiatryAndFamilyMedicine ||
            userProgram.trainingProgramTypeId === TrainingProgramTypes.PsychiatryAndInternalMedicine) && pgy.sequenceNumber === 1) return true;

        if (userProgram.trainingProgramTypeId == TrainingProgramTypes.ChildNeurologyAndNeurodevelopmentalDisabilities
            && userProgramType?.isCombinedProgram 
            && pgy.sequenceNumber === 1) return false;

        if (userProgramType?.isCombinedProgram && pgy.sequenceNumber === 1) return true;

        if (userProgram.trainingProgramTypeId === TrainingProgramTypes.PostPediatricPortal &&
            (pgy.sequenceNumber === 1 || pgy.sequenceNumber === 2 || pgy.sequenceNumber === 3)) return true;

        return false;
    }

    const saveFastTrackStatus = async (e: React.ChangeEvent<HTMLInputElement>) => {
        let isOnFastTrack = await abpnClient.post<boolean>(`/api/training/saveFastTrackStatus?physicianId=${props.physicianId}&programTypeId=${userTrainingProgramType.trainingProgramTypeId}`, e.target.checked, authContext);
        props.saveResident(null, null, isOnFastTrack);
    }

    const savePostGraduateYear = () => {
        const form = editForm.current;
        let postGraduateYear = postGraduateYears.find(_ => _.inEditMode);
        setAttestationValidationErrors([]);
        setShowInvalidAttestationAlert(false);

        validateMonths(postGraduateYear);
        validateDates(postGraduateYear);
        validateProgram(postGraduateYear);
        validateAttestation(postGraduateYear);

        form.classList.add('was-validated');

        if (form.checkValidity()) {
            abpnClient.post<PostGraduateYear>(`/api/training/savePostGraduateYear`, postGraduateYear, authContext).then(updatedYear => {
                props.setIsEditing(false);
                let updatedPgys = deepCopy(postGraduateYears);
                let editIndex = updatedPgys.findIndex(_ => _.inEditMode);
                updatedPgys[editIndex] = updatedYear;
                let sortedData = sortData(updatedPgys);
                setPostGraduateYears(sortedData);
                setOriginalPostGraduateYears(sortedData);
                setPgySelectYearLookupList(getAvailableYears(postGraduateYears));
                props.saveResident(sortedData, null, null);
                form.classList.remove('was-validated');
                reloadRoster();
            });
        }
    }

    const addTrainingDocumentToPgy = (event: React.FormEvent<HTMLInputElement>, postGraduateYear: PostGraduateYear) => {
        setShowInvalidUploadAlert(false);
        reloadRoster();
        createTrainingDocument(event.currentTarget.files[0], event.currentTarget.files[0].name, postGraduateYear);
        event.preventDefault();
        event.stopPropagation();
    }

    const createTrainingDocument = (document: File, documentName: string, postGraduateYear: PostGraduateYear) => {
        if (document.type !== "application/pdf" || document.size > 5000000) {
            setShowInvalidUploadAlert(true);
            return;
        }
        let newTrainingDocFormData = new FormData();
        newTrainingDocFormData.append('document', document);
        newTrainingDocFormData.append('TrainingId', postGraduateYear.trainingId.toString());
        newTrainingDocFormData.append('documentName', documentName);

        saveNewTrainingDocument(newTrainingDocFormData, postGraduateYear);
    }

    const saveNewTrainingDocument = (form: FormData, pgy: PostGraduateYear) => {
        const url = `/api/training/createTrainingDocument?physicianId=` + props.physicianId;
        abpnClient.postForm<FormData>(url, form, authContext).then(() => {
            getUpdatedTrainingDocuments(pgy);
        });
    }

    const getUpdatedTrainingDocuments = (pgy: PostGraduateYear) => {
        const url = `/api/training/GetTrainingDocuments?physicianId=` + props.physicianId;
        abpnClient.get<Array<TrainingDocument>>(url, authContext).then(documents => {
            documents = documents.filter(_ => _.trainingId == pgy.trainingId);
            props.saveTrainingDocuments(documents, pgy);
            validatePgyDocuments(pgy);
        });
    }

    const download = async (e: React.MouseEvent<HTMLElement, MouseEvent>, trainingDocument: TrainingDocument) => {
        e.preventDefault();
        e.stopPropagation();
        const url = "/api/Training/DownloadTrainingDocument?trainingDocumentId=" + trainingDocument.trainingDocumentId;
        var response = await abpnClient.getResponse(url, authContext)
        var blob = await response.blob();
        const downloadFileUrl = window.URL.createObjectURL(
            new Blob([blob]),
        );
        const link = document.createElement('a');
        link.href = downloadFileUrl;
        link.setAttribute(
            'download',
            trainingDocument.documentName,
        );
        // Append to html link element page
        document.body.appendChild(link);
        // Start download
        link.click();
        // Clean up and remove the link
        link.parentNode.removeChild(link);
    }

    const confirmDeleteDocument = (document: TrainingDocument, isConfirming: boolean) => {
        setDocumentToDelete(document);
        setIsConfirmingDeleteDocument(isConfirming);
    }

    const deleteTrainingDocument = () => {
        setIsConfirmingDeleteDocument(false);
        documentToDelete.isDeleted = true;
        let pgy = postGraduateYears.find(_ => _.trainingId === documentToDelete.trainingId);
        const url = `/api/training/updateTrainingDocument?physicianId=` + props.physicianId;
        abpnClient.post<TrainingDocument>(url, documentToDelete, authContext).then(() => {
            removeDeletedTrainingDocument(documentToDelete.trainingDocumentId, pgy);
        });
    }

    const removeDeletedTrainingDocument = (trainingDocumentId: number, pgy: PostGraduateYear) => {
        let updatedPostGraduateYears = deepCopy(postGraduateYears);
        let updatedTrainingDocuments = updatedPostGraduateYears.find(_ => _.trainingId === pgy.trainingId).trainingDocuments.filter(_ => _.trainingDocumentId !== trainingDocumentId);
        updatedPostGraduateYears.find(_ => _.trainingId === pgy.trainingId).trainingDocuments = updatedTrainingDocuments;

        setPostGraduateYears(updatedPostGraduateYears);
        setDocumentToDelete(null);
        props.saveTrainingDocuments(updatedTrainingDocuments, pgy);
        validatePgyDocuments(pgy);
    }

    const validateMonths = (postGraduateYear: PostGraduateYear) => {
        if (postGraduateYear.months > 13) {
            ValidatorHelper.setCustomValidationWithFeedback(pgyMonthsRef.current, "Months/Blocks must be less than or equal to 13");
        }

        if (postGraduateYear.months && postGraduateYear.months % 1 !== 0) {
            ValidatorHelper.setCustomValidationWithFeedback(pgyMonthsRef.current, "Months/Blocks can't be decimal");
        }
    }

    const validateProgram = (postGraduateYear: PostGraduateYear) => {
        let programMatch = programList.find(_ => _.trainingProgramId === postGraduateYear.trainingProgramId);

        if (!programMatch) {
            showOtherProgramTypeForPGY(true);
            let pgyProgramCodeElement: HTMLInputElement = document.getElementById(programCodeId) as HTMLInputElement;
            ValidatorHelper.setCustomValidationWithFeedback(pgyProgramCodeElement, "The program you entered is not defined in preCERT. If you entered the number incorrectly, please try again. If you entered it correctly, please check this box to define this PGY as 'Other'");
        } else {
            showOtherProgramTypeForPGY(false);
        }
    }

    const totalMonths = (postGraduateYear: PostGraduateYear) => {
        let currentPgys = postGraduateYears.filter(_ => _.sequenceNumber === postGraduateYear.sequenceNumber);
        let startDate = new Date(currentPgys[0].startDate);
        let endDate = new Date(currentPgys[currentPgys.length -1].endDate);
        return endDate.getMonth() - startDate.getMonth() + (12 * (endDate.getFullYear() - startDate.getFullYear()));
    }

    const validateDates = (postGraduateYear: PostGraduateYear) => {
        let pgyEndDateFormElement: HTMLInputElement = document.getElementById(pgyEndDateFormId) as HTMLInputElement;
        let pgyStartDateFormElement: HTMLInputElement = document.getElementById(pgyStartDateFormId) as HTMLInputElement;
        let today = new Date();
        today.setHours(0,0,0,0);

        if (!postGraduateYear.startDate || !postGraduateYear.endDate) return;

        if (new Date(postGraduateYear.startDate) > maxPgyStartDate) {
            ValidatorHelper.setCustomComponentValidation(pgyStartDateFormElement, `Start Date must be before ${maxPgyStartDate.toLocaleDateString()}`);
            return;
        }

        if (new Date(postGraduateYear.endDate) < new Date(postGraduateYear.startDate)) {
            ValidatorHelper.setCustomComponentValidation(pgyEndDateFormElement, "End Date must be after the start date");
            return;
        }

        if (postGraduateYear.isAttestedComplete && new Date(postGraduateYear.endDate) >= new Date(today)) {
            ValidatorHelper.setCustomComponentValidation(pgyEndDateFormElement, "PGY must have an End Date prior to the attestation date.");
            return;
        }

        if (totalMonths(postGraduateYear) >= 24) {
            ValidatorHelper.setCustomComponentValidation(pgyEndDateFormElement, "End date can not be greater than 2 years from the start date")
        }

        let overlapPgyStartDate = originalPostGraduateYears.filter(_ => _.trainingId !== postGraduateYear.trainingId).find(_ => new Date(_.startDate) >= new Date(postGraduateYear.startDate) && new Date(_.startDate) <= new Date(postGraduateYear.endDate));
        let overlapPgyEndDate = originalPostGraduateYears.filter(_ => _.trainingId !== postGraduateYear.trainingId).find(_ => new Date(_.endDate) >= new Date(postGraduateYear.startDate) && new Date(_.endDate) <= new Date(postGraduateYear.endDate));
        let insidePgyDates = originalPostGraduateYears.filter(_ => _.trainingId !== postGraduateYear.trainingId).find(_ => new Date(_.startDate) <= new Date(postGraduateYear.startDate) && new Date(_.endDate) >= new Date(postGraduateYear.endDate));

        if (overlapPgyStartDate || overlapPgyEndDate || insidePgyDates) {
            ValidatorHelper.setCustomComponentValidation(pgyEndDateFormElement, "Start and end dates of PG years can not overlap");
        }
    }

    const validateAttestation = (postGraduateYear: PostGraduateYear) => {
        let pgyDocumentsElement: HTMLInputElement = document.getElementById(trainingDocumentsId) as HTMLInputElement;
        let customErrorMsgControl = pgyDocumentsElement.parentElement.parentElement.parentElement.getElementsByClassName("invalid-feedback")[0];

        if (isAttestationCheckboxDisabled && postGraduateYear.isAttestedComplete) {
            ValidatorHelper.setCustomValidationWithFeedback(pgyDocumentsElement, "Attestation requires documentation of training requirements completed in this PGY. Save PGY, upload documentation, and then attest", customErrorMsgControl);
        }

        if (!(postGraduateYear.months > 0) && postGraduateYear.isAttestedComplete) {
            ValidatorHelper.setCustomValidationWithFeedback(pgyMonthsRef.current, "Cannot attest with 0 months / blocks");
        }

    }

    const validatePgyDocuments = (pgy: PostGraduateYear) => {
        if (user.selectedTrainingProgramId !== pgy.trainingProgramId && pgy.sequenceNumber === 1) {
            let hasDocuments = pgy.trainingDocuments != null;

            if (!hasDocuments) {
                setIsAttestationCheckboxDisabled(true);
                return;
            }
        }

        clearDocumentValidation();
        setIsAttestationCheckboxDisabled(false);
    }

    const sortData = (data: Array<PostGraduateYear>) => {
        return data?.sort((a, b) => (new Date(a.endDate)).getTime() - (new Date(b.endDate)).getTime());
    }

    const addPostGraduateYear = async () => {
        let userProgramType = deepCopy(userTrainingProgramType);
        let trainingPrograms = deepCopy(programList);

        if (!programList?.length) trainingPrograms = await getTrainingPrograms();
        if (!userTrainingProgramType) userProgramType = await getTrainingProgramType();

        const userProgram = trainingPrograms.find(_ => _.trainingProgramId === user.selectedTrainingProgramId);
        const maxSequenceNumber = Math.max(...props.pgyLookupList.map(_ => _.id));

        let newSequenceNumber = postGraduateYears?.length > 0 ? postGraduateYears[postGraduateYears.length - 1].sequenceNumber + 1 : 1;
        let newPgy = new PostGraduateYear(props.physicianId);
        newPgy.startDate = (new Date(today.getFullYear(), 6, 1)).toJSON();
        newPgy.endDate = (new Date(today.getFullYear() + 1, 5, 30)).toJSON();
        newPgy.sequenceNumber = newSequenceNumber > maxSequenceNumber ? maxSequenceNumber : newSequenceNumber;
        newPgy.trainingProgramId = userProgram.trainingProgramId;
        newPgy.trainingProgramName1 = userProgram.trainingProgramName1;
        newPgy.trainingProgramCode = userProgram.trainingProgramCode;
        newPgy.documentReviewStatus = DocumentReviewStatus.NoDocuments;

        if (newPgy.trainingProgramId) {
            newPgy.trainingProgramCode = trainingPrograms.find(_ => _.trainingProgramId === newPgy.trainingProgramId).trainingProgramCode;
        }

        let updatedPgys = deepCopy(postGraduateYears);
        updatedPgys.push(newPgy);

        let enableSelectOtherProgram = canSelectOtherProgram(newPgy, userProgramType);
        setCanSelectOtherProgramForPgyEdit(enableSelectOtherProgram);
        setPostGraduateYears(updatedPgys);
        setIsAttestationCheckboxDisabled(false);
    }

    const resetDefaultProgram = () => {
        const userProgram = programList.find(_ => _.trainingProgramId === user.selectedTrainingProgramId);
        let updatedPgys = deepCopy(postGraduateYears);
        let updatedPgy = updatedPgys.find(_ => _.inEditMode);

        if (!updatedPgy) return;
        updatedPgy.trainingProgramId = user.selectedTrainingProgramId;
        updatedPgy.trainingProgramName1 = userProgram.trainingProgramName1;
        updatedPgy.trainingProgramCode = userProgram.trainingProgramCode;
        setPostGraduateYears(updatedPgys);
    }

    const deletePostGraduateYear = async () => {
        setIsConfirmingDeletePgy(false);
        abpnClient.post<PostGraduateYear>(`/api/training/deletePostGraduateYear`, pgyToDelete, authContext).then(() => {
            removeDeletedPostGraduateYear(pgyToDelete.trainingId);
            setPgyToDelete(null);
            reloadRoster();
        });
    }

    const confirmDeletePostGraduateYear = (pgy: PostGraduateYear, isConfirming: boolean) => {
        setPgyToDelete(pgy);
        setIsConfirmingDeletePgy(isConfirming);
    }

    const removeDeletedPostGraduateYear = (trainingId: number) => {
        let currentPostGraduateYears = deepCopy(postGraduateYears);
        var updatedPostGraduateYears = currentPostGraduateYears.filter(_ => _.trainingId !== trainingId);
        setPostGraduateYears(updatedPostGraduateYears);
        setOriginalPostGraduateYears(updatedPostGraduateYears);
        props.saveResident(updatedPostGraduateYears, null, null);
    }
    const startEdit = async (pgy: PostGraduateYear) => {
        props.setIsEditing(true);
        let userProgramType = deepCopy(userTrainingProgramType);
        if (!userTrainingProgramType) userProgramType = await getTrainingProgramType();

        let enableSelectOtherProgram = canSelectOtherProgram(pgy, userProgramType);
        setCanSelectOtherProgramForPgyEdit(enableSelectOtherProgram);

        let updatedPgys = deepCopy(postGraduateYears);
        let editIndex = updatedPgys.findIndex(_ => _.trainingId === pgy.trainingId);
        updatedPgys[editIndex] = deepCopy(pgy);
        updatedPgys[editIndex].inEditMode = true;

        validatePgyDocuments(updatedPgys[editIndex]);
        setPostGraduateYears(updatedPgys);
    }

    const cancelEdit = () => {
        props.setIsEditing(false);
        setAttestationValidationErrors([]);
        setShowInvalidAttestationAlert(false);
        editForm.current.classList.remove('was-validated');
        const originalData = deepCopy(originalPostGraduateYears);
        setPostGraduateYears(originalData);
    }

    const showOtherProgramTypeForPGY = (show: boolean) => {
        updatePostGraduateYear((_: PostGraduateYear) => {
            _.showOtherProgram = show;
        });
    }

    const toggleShowDocuments = (pgy: PostGraduateYear) => {
        let updatedPgys = deepCopy(postGraduateYears);
        let updatePgy = updatedPgys.find(_ => _.trainingId === pgy.trainingId);
        updatePgy.viewDocuments = !updatePgy.viewDocuments;
        setPostGraduateYears(updatedPgys);
    }

    const updatePostGraduateYear = (updateFunction: (n: PostGraduateYear) => void): PostGraduateYear => {
        var updatePgys = deepCopy(postGraduateYears);
        var postGraduateYear = updatePgys.find(_ => _.inEditMode);
        updateFunction(postGraduateYear);

        setPostGraduateYears(updatePgys);
        validatePgyDocuments(postGraduateYear);

        return postGraduateYear;
    }

    const updateSequenceNumber = (e: React.ChangeEvent<HTMLSelectElement>) => {
        updatePostGraduateYear((_: PostGraduateYear) => {
            var pgy = e.target.value ? props.pgyLookupList.find((_: LookupItem) => _.id === parseInt(e.target.value)) : null;
            _.sequenceNumber = pgy?.id;

            let enableSelectOtherProgram = canSelectOtherProgram(_, userTrainingProgramType);
            if (!enableSelectOtherProgram && _.trainingProgramId !== user.selectedTrainingProgramId) resetDefaultProgram();
            setCanSelectOtherProgramForPgyEdit(enableSelectOtherProgram);
        });
    }

    const clearDateValidation = () => {
        let pgyStartDateFormElement: HTMLInputElement = document.getElementById(pgyStartDateFormId) as HTMLInputElement;
        let pgyEndDateFormElement: HTMLInputElement = document.getElementById(pgyEndDateFormId) as HTMLInputElement;

        ValidatorHelper.clearCustomComponentValidation(pgyStartDateFormElement);
        ValidatorHelper.clearCustomComponentValidation(pgyEndDateFormElement);
    }

    const clearMonthsBlocksValidation = () => {
        ValidatorHelper.clearCustomValidation(pgyMonthsRef.current);
    }

    const clearDocumentValidation = () => {
        let pgyDocumentsElement: HTMLInputElement = document.getElementById(trainingDocumentsId) as HTMLInputElement;
        ValidatorHelper.clearCustomValidation(pgyDocumentsElement);
    }

    const updateStartDate = (newDate: Date) => {
        updatePostGraduateYear((_: PostGraduateYear) => _.startDate = newDate ? newDate.toJSON() : null);
        clearDateValidation();
    }

    const updateEndDate = (newDate: Date) => {
        updatePostGraduateYear((_: PostGraduateYear) => _.endDate = newDate ? newDate.toJSON() : null);
        clearDateValidation();
    }

    const updateMonths = (event: React.ChangeEvent<HTMLInputElement>) => {
        ValidatorHelper.clearCustomValidation(pgyMonthsRef.current);
        updatePostGraduateYear((_: PostGraduateYear) => {
            _.months = parseFloat(event.target.value);
        })
    }

    const updateProgramCode = (event: React.ChangeEvent<HTMLInputElement>, isOtherType: boolean = false) => {
        let programMatch = programList.find(_ => _.trainingProgramCode === event.target.value);
        let otherProgram = programList.find(_ => _.trainingProgramTypeId === TrainingProgramTypes.Other && _.trainingProgramCode === UnfoundProgramDefaultCode);
        let defaultProgramId: number | null = isOtherType ? otherProgram.trainingProgramId : null;
        let pgyProgramCodeElement: HTMLInputElement = document.getElementById(programCodeId) as HTMLInputElement;
        ValidatorHelper.clearCustomValidation(pgyProgramCodeElement);

        updatePostGraduateYear((_: PostGraduateYear) => {
            if (isOtherType) {
                _.showOtherProgram = false;
            }
            _.trainingProgramId = programMatch ? programMatch.trainingProgramId : defaultProgramId;
            _.trainingProgramCode = isOtherType ? UnfoundProgramDefaultCode : event.target.value;
        });
    }

    const updateIsAttested = () => {
        updatePostGraduateYear((_: PostGraduateYear) => {
            _.isAttestedComplete = !_.isAttestedComplete;
        })
        clearDateValidation();
        clearMonthsBlocksValidation();
        clearDocumentValidation();
    }

    const savePgyAttestation = async (pgy: PostGraduateYear) => {
        if(!pgy.inEditMode){
            let validationErrors = [];
            const form = editForm.current;
            pgy.isAttestedComplete = true;

            let today = new Date();
            today.setHours(0,0,0,0);

            if (pgy.months > 13) {
                validationErrors.push("Months/Blocks must be less than or equal to 13");
            }

            if (pgy.months && pgy.months % 1 !== 0) {
                validationErrors.push("Months/Blocks can't be decimal");
            }

            if (!pgy.startDate || !pgy.endDate) {
                validationErrors.push("PGY must have a Start and End Date to be attested.");
            }

            if (new Date(pgy.startDate) > maxPgyStartDate) {
                validationErrors.push(`Start Date must be before ${maxPgyStartDate.toLocaleDateString()}`);
            }

            if (new Date(pgy.endDate) < new Date(pgy.startDate)) {
                validationErrors.push("End Date must be after the start date");
            }

            if (new Date(pgy.endDate) > new Date(today)) {
                validationErrors.push("PGY must have an End Date prior to the attestation date.");
            }

            let overlapPgyStartDate = originalPostGraduateYears.filter(_ => _.trainingId !== pgy.trainingId).find(_ => new Date(_.startDate) >= new Date(pgy.startDate) && new Date(_.startDate) <= new Date(pgy.endDate));
            let overlapPgyEndDate = originalPostGraduateYears.filter(_ => _.trainingId !== pgy.trainingId).find(_ => new Date(_.endDate) >= new Date(pgy.startDate) && new Date(_.endDate) <= new Date(pgy.endDate));
            let insidePgyDates = originalPostGraduateYears.filter(_ => _.trainingId !== pgy.trainingId).find(_ => new Date(_.startDate) <= new Date(pgy.startDate) && new Date(_.endDate) >= new Date(pgy.endDate));

            if (overlapPgyStartDate || overlapPgyEndDate || insidePgyDates) {
                validationErrors.push("Start and end dates of PG years can not overlap");
            }

            let hasDocuments = pgy.trainingDocuments != null;
            if (user.selectedTrainingProgramId !== pgy.trainingProgramId && pgy.sequenceNumber === 1 && !hasDocuments) {
                setIsAttestationCheckboxDisabled(true);
                validationErrors.push("Attestation requires documentation of training requirements completed in this PGY. Save PGY, upload documentation, and then attest");
            } else {
                setIsAttestationCheckboxDisabled(false);
            }

            if (!(pgy.months > 0)) {
                validationErrors.push("Cannot attest with 0 months / blocks");
            }

            if(validationErrors.length > 0) {
                setAttestationValidationErrors(validationErrors);
                setShowInvalidAttestationAlert(true);
                startEdit(pgy);
            }
            else{
                if (form.checkValidity()) {
                    abpnClient.post<PostGraduateYear>(`/api/training/savePostGraduateYear`, pgy, authContext).then(updatedYear => {
                        let updatedPgys = deepCopy(postGraduateYears);
                        let editIndex = updatedPgys.findIndex(_ => _.trainingId === pgy.trainingId);
                        updatedPgys[editIndex] = updatedYear;
                        let sortedData = sortData(updatedPgys);
                        setPostGraduateYears(sortedData);
                        setOriginalPostGraduateYears(sortedData);
                        setPgySelectYearLookupList(getAvailableYears(postGraduateYears));
                        props.saveResident(sortedData, null, null);
                        form.classList.remove('was-validated');
                        reloadRoster();
                    });
                }
            }

        } else {
            updateIsAttested();
        }
    }

    const renderSubComponent = (row: Row<PostGraduateYear>) => {
        let pgyDocuments = row.original.trainingDocuments;

        return (
            <>
                <tr>
                    <td colSpan={8}>
                        <h6 className="fw-bolder ml-2 mt-1 text-secondary">Documents&nbsp;
                        <label
                            className="link-primary me-2 pc-label"
                            hidden={props.isReadonly || row.original.inEditMode || props.programTypeIdsWithApprovedApplication.find(_ => _ === row.original?.ownedByTrainingProgramTypeId) != null || isDocumentUploadButtonHidden(row.original)}
                        >
                            <u>Upload</u>
                            <input type="file" style={{ display: "none" }} accept="application/pdf" className="form-control" onChange={(event) => addTrainingDocumentToPgy(event, row.original)} />
                        </label>
                        </h6>
                    </td>
                </tr>

                {pgyDocuments.map((doc, key) => {
                    return (
                        <tr key={key} className={doc.source === SourceType.ABPN ? 'pc-table-row-abpn-source' : ''}>
                            <td colSpan={7}>
                                <a target="_blank" href="#" onClick={(event) => download(event, doc)}>{doc.documentName}</a>
                            </td>
                            <td colSpan={1}>
                                <button type="button" title="Delete" hidden={isDocumentDeleteButtonHidden(doc, row.original)}
                                    className="btn btn-sm btn-clean btn-icon btn-icon-md" onClick={() => confirmDeleteDocument(doc, true)}>
                                    <i className="la la-trash"></i>
                                </button>
                                {doc.source === SourceType.ABPN &&
                                    <OverlayTrigger overlay={<Tooltip bsPrefix="pc-tooltip">Document uploaded by ABPN.</Tooltip>}>
                                        <i className="las la-info-circle ml-1 text-pc-secondary"></i>
                                    </OverlayTrigger>
                                }
                            </td>
                        </tr>
                    );
                })}
            </>
        );
    }

    let accumalatedTotalAttestedMonths = postGraduateYears.reduce((accumulator, object) => {
        return accumulator + (object?.months ?? 0);
    }, 0);

    const getRowProps = (row: any) => {
        const { original: pgy }: { original: PostGraduateYear } = row;
        let prop = {};

        if( pgy.source === SourceType.ABPN)
            prop = { className: 'pc-table-row-abpn-source' }

        else if( pgy.documentReviewStatus === DocumentReviewStatus.Approved)
            prop = { className: 'pgy-status-approved' }

        else if( pgy.documentReviewStatus === DocumentReviewStatus.Denied || pgy.documentReviewStatus === DocumentReviewStatus.Hold || pgy.documentReviewStatus === DocumentReviewStatus.HoldWithChanges)
            prop = { className: 'pgy-status-denied' }

        return prop;
    }

    const isPGYEditButtonHidden = (pgy: PostGraduateYear) => {
        let result = props.isReadonly
        || (props.programTypeIdsWithApprovedApplication.find(_ => _ === pgy?.ownedByTrainingProgramTypeId) != null)
        || (postGraduateYears.find(_ => _.inEditMode) != null)
        || pgy.source === SourceType.ABPN
        || (pgy.isAttestedComplete && user?.userRole?.isProgramCoordinator)
        || pgy.ownedByTrainingProgramId !== user?.selectedTrainingProgramId
        || props.isAlumni
        || pgy.documentReviewStatus == DocumentReviewStatus.Approved;

        return result;
    }

    const isPGYDeleteButtonHidden = (pgy: PostGraduateYear) => {
        return pgy.source === SourceType.ABPN
        || (props.programTypeIdsWithApprovedApplication.find(_ => _ === pgy?.ownedByTrainingProgramTypeId) != null)
        || (pgy.isAttestedComplete && user?.userRole?.isProgramCoordinator)
        || pgy.ownedByTrainingProgramId !== user?.selectedTrainingProgramId 
        || props.isAlumni
        || pgy.documentReviewStatus == DocumentReviewStatus.Approved
        || pgy.documentReviewStatus === DocumentReviewStatus.HoldWithChanges
        || pgy.documentReviewStatus === DocumentReviewStatus.Hold
        || pgy.documentReviewStatus === DocumentReviewStatus.Denied;
    }

    const isDocumentUploadButtonHidden = (pgy: PostGraduateYear) => {
        return props.isReadonly
        || pgy.inEditMode
        || (props.programTypeIdsWithApprovedApplication.find(_ => _ === pgy?.ownedByTrainingProgramTypeId) != null)
        || pgy.documentReviewStatus == DocumentReviewStatus.Approved;
    }
    const isDocumentDeleteButtonHidden = (doc: TrainingDocument, pgy: PostGraduateYear) => {
        return doc.source === SourceType.ABPN
        || (props.programTypeIdsWithApprovedApplication.find(_ => _ === pgy?.ownedByTrainingProgramTypeId) != null)
        || pgy.ownedByTrainingProgramId !== user?.selectedTrainingProgramId
        || props.isAlumni
        || pgy.documentReviewStatus == DocumentReviewStatus.Approved
        || pgy.documentReviewStatus === DocumentReviewStatus.HoldWithChanges
        || pgy.documentReviewStatus === DocumentReviewStatus.Hold
        || pgy.documentReviewStatus === DocumentReviewStatus.Denied;
    }


    const getAttestedCheckBox = (pgy: PostGraduateYear) => {
        return <Form.Check type="checkbox">
            <Form.Check.Input checked={pgy.isAttestedComplete} defaultChecked={pgy.isAttestedComplete} disabled={checkAttestationCheckboxDisabled(pgy)} className="pc-checkbox" type="checkbox" onChange={() => savePgyAttestation(pgy)} />
        </Form.Check>
    }

    const getOverlayAndCheckbox = (pgy: PostGraduateYear) => { 
        let pgyDocuments = pgy.trainingDocuments ?? [];
        let otherTrainingProgram = user.trainingPrograms.some(_ => _.trainingProgramCode === pgy.trainingProgramCode);
        if ((!otherTrainingProgram && pgy.ownedByTrainingProgramId === user?.selectedTrainingProgramId) && pgyDocuments.length === 0) {
            return <OverlayTrigger placement="right" overlay={<Tooltip id="cannot_attest_to_pgy" bsPrefix="pc-tooltip">Attestation requires documentation of training requirements completed in this PGY. Save PGY, upload documentation, and then attest</Tooltip>}>
                <span className="d-inline-block">{getAttestedCheckBox(pgy)}</span>
            </OverlayTrigger> 
        }           
        if((pgy.ownedByTrainingProgramId !== user?.selectedTrainingProgramId) && pgy.ownedByTrainingProgramId !== undefined) {
            return <div className="text-start"> No &nbsp;
                <OverlayTrigger placement="right" overlay={<Tooltip bsPrefix="pc-tooltip">Attestation of this PGY must be done by the PD of the training program that created it.</Tooltip>}>   
                    <i className="las la-info-circle ml-1 text-pc-secondary"></i>
                </OverlayTrigger> 
            </div>
        }
        else { 
            return getAttestedCheckBox(pgy);
        }
    }

    const checkAttestationCheckboxDisabled = (pgy: PostGraduateYear) => {
        let pgyInEdit = postGraduateYears?.filter(_ => _.inEditMode);
        let otherTrainingProgram = user.trainingPrograms.some(_ => _.trainingProgramCode === pgy.trainingProgramCode);
        if((pgy.sequenceNumber === 1 && pgy.ownedByTrainingProgramId !== user.selectedTrainingProgramId ) || (!otherTrainingProgram && pgy.ownedByTrainingProgramId === user?.selectedTrainingProgramId)) {
            let pgyDocuments = pgy.trainingDocuments ?? [];
            return user?.userRole.isProgramCoordinator || pgyDocuments?.length === 0 || (pgyInEdit?.length===1 && !pgy.inEditMode);
        }
        return (pgy.ownedByTrainingProgramId !== user?.selectedTrainingProgramId) && ((pgyInEdit?.length === 1 && !pgy.inEditMode)) || user?.userRole?.isProgramCoordinator;
    }

    const getInfoIcon = (pgy: PostGraduateYear) => {
        if(pgy.documentReviewStatus === DocumentReviewStatus.Approved) {
            return <OverlayTrigger overlay={<Tooltip bsPrefix="pc-tooltip">PGY document(s) approved by ABPN.</Tooltip>}>
                <i className="las la-info-circle ml-1 text-pc-secondary"></i>
            </OverlayTrigger>
        }
        
        else if(pgy.documentReviewStatus === DocumentReviewStatus.Hold || pgy.documentReviewStatus === DocumentReviewStatus.HoldWithChanges) {
            return <OverlayTrigger overlay={<Tooltip bsPrefix="pc-tooltip">PGY document(s) on hold while being reviewed by ABPN.</Tooltip>}>
                <i className="las la-info-circle ml-1 text-pc-secondary"></i>
            </OverlayTrigger>
        }
        else if(pgy.documentReviewStatus === DocumentReviewStatus.Denied) {
            return <OverlayTrigger overlay={<Tooltip bsPrefix="pc-tooltip">PGY document(s) denied by ABPN.  Next steps are being communicated via email between ABPN and your program’s PD.</Tooltip>}>
                <i className="las la-info-circle ml-1 text-pc-secondary"></i>
            </OverlayTrigger>
        }
        else if(pgy.source === SourceType.ABPN) {
            return <OverlayTrigger overlay={<Tooltip bsPrefix="pc-tooltip">PGY entered by ABPN.</Tooltip>}>
                <i className="las la-info-circle ml-1 text-pc-secondary"></i>
            </OverlayTrigger>
        }
    }

    const columns = [
        {
            Header: "Year",
            width: "80px",
            filterable: false,
            sortable: true,
            accessor: (pgy: PostGraduateYear): any => {
                if (pgy.inEditMode) {
                    return (
                        <select required
                            className="form-control form-control-sm"
                            key={pgy.sequenceNumber}
                            defaultValue={pgy.sequenceNumber != null ? props.pgyLookupList.find(_ => _.id === pgy.sequenceNumber).id : ""}
                            onChange={e => updateSequenceNumber(e)} >
                            <option value="">Select</option>
                            {pgySelectYearLookupList.map(_ => (<option value={_.id} key={_.id}>{_.name}</option>))}
                        </select>)
                }
                return pgy.sequenceNumber ? `PGY-${pgy.sequenceNumber}` : '';
            }
        },
        {
            Header: "Program",
            width: "auto",
            filterable: false,
            sortable: false,
            filter: "fuzzyText",
            accessor: (pgy: PostGraduateYear): any => {
                if (pgy.inEditMode && canSelectOtherProgramForPgyEdit) {
                    return (
                        <>
                            <Form.Group controlId={programCodeId}>
                                <Form.Control
                                    defaultValue={pgy.trainingProgramCode}
                                    value={pgy.trainingProgramCode}
                                    type="text"
                                    placeholder="Enter ACGME Program Number"
                                    onChange={(e: any) => { updateProgramCode(e) }}>
                                </Form.Control>
                                <span className="invalid-feedback">No program found</span>
                            </Form.Group>
                            {pgy.showOtherProgram &&
                                <Form.Check type="checkbox">
                                    <Form.Check.Label className="pc-font-size-smaller pc-validation-text"> Other Program
                                        <Form.Check.Input className="pc-checkbox pc-required-validation" type="checkbox" required={pgy.showOtherProgram} onChange={(e: any) => updateProgramCode(e, true)} />
                                    </Form.Check.Label>
                                </Form.Check>
                            }
                        </>

                    )
                }
                return `${pgy?.trainingProgramCode === UnfoundProgramDefaultCode ? `${UnfoundProgramDefaultCode}` : `(${pgy.trainingProgramCode}) ${pgy.trainingProgramName1}`}`;
            },
        },
        {
            Header: "Start",
            width: "120px",
            filterable: false,
            sortable: false,
            filter: "fuzzyText",
            id: "pgyStartDate",
            accessor: (pgy: PostGraduateYear) => {
                var startDate = pgy.startDate ? new Date(pgy.startDate) : null;
                if (pgy.inEditMode) {
                    return (
                        <div>
                            <DatePicker
                                className="bc-datepicker form-control form-control-sm"
                                id="postGraduateYearFormStartDate"
                                peekNextMonth
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                selected={startDate}
                                onChange={newDate => updateStartDate(newDate)}
                                placeholderText="mm/dd/yyyy" />
                            <span className="invalid-feedback"></span>
                        </div>
                    )
                }
                return startDate ? <div className="text-start">{getDateWithOffset(pgy.startDate).toLocaleDateString()}</div> : null;
            },
        },
        {
            Header: "End",
            width: "120px",
            filterable: false,
            sortable: false,
            filter: "fuzzyText",
            id: "pgyEndDate",
            accessor: (pgy: PostGraduateYear) => {
                var endDate = pgy.endDate ? new Date(pgy.endDate) : null;
                if (pgy.inEditMode) {
                    return (
                        <div>
                            <DatePicker
                                className="bc-datepicker form-control form-control-sm"
                                id="postGraduateYearFormEndDate"
                                peekNextMonth
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                selected={endDate}
                                onChange={newDate => updateEndDate(newDate)}
                                placeholderText="mm/dd/yyyy" />
                            <span className="invalid-feedback"></span>
                        </div>
                    )
                }
                return endDate ? <div className="text-start">{getDateWithOffset(pgy.endDate).toLocaleDateString()}</div> : null;
            },
        },
        {
            Header: "Months / Blocks",
            width: "110px",
            filterable: false,
            sortable: false,
            filter: "fuzzyText",
            accessor: (pgy: PostGraduateYear) => {
                return pgy.inEditMode ? (
                    <>
                        <Form.Control
                            defaultValue={pgy.months}
                            ref={pgyMonthsRef}
                            type="number"
                            min={0}
                            max={13}
                            onChange={(e: any) => { updateMonths(e) }}>
                        </Form.Control>
                        <span className="invalid-feedback"></span>
                    </>
                ) : <div className="text-start">{pgy.months?.toString() ?? ""}</div>;
            }
        },
        {
            Header: "Documents",
            HeaderInfo: "For residents who transfer programs, upload PDF documentation of the resident’s training rotation for each respective PG year. The rotation letter/schedule must be signed by the Program Director.",
            width: "130px",
            id: "DocumentName",
            accessor: (pgy: PostGraduateYear) => {
                let pgyDocuments = pgy.trainingDocuments ?? [];
                return pgy.inEditMode ? (
                    <div id={trainingDocumentsErrorId}>
                        <div>
                            {pgyDocuments?.length ?? 0} <a type="button" title="Documents" hidden={!pgyDocuments?.length} className="link-primary ms-2 me-2" onClick={() => toggleShowDocuments(pgy)}>
                                                        {pgy.viewDocuments ? 'Hide' : 'Show'}
                                                    </a>
                            <label
                                hidden={pgyDocuments?.length > 0 || pgy.inEditMode || (props.programTypeIdsWithApprovedApplication.find(_ => _ === pgy?.ownedByTrainingProgramTypeId) != null) || isDocumentUploadButtonHidden(pgy)}
                                className="link-primary ms-2 me-2 pc-label"
                            >
                                <u>Upload</u>
                                <input id={trainingDocumentsId} type="file" style={{ display: "none" }} accept="application/pdf" className="form-control" onChange={(event) => addTrainingDocumentToPgy(event, pgy)} />
                            </label>
                        </div>
                        <span className="invalid-feedback"></span>
                    </div>
                ) :
                    (<div>
                        {pgyDocuments?.length ?? 0} <a type="button" title="Documents" hidden={!pgyDocuments?.length} className="link-primary ms-2 me-2" onClick={() => toggleShowDocuments(pgy)}>
                            {pgy.viewDocuments ? 'Hide' : 'Show'}
                        </a>
                    </div>);
            }
        },
        {
            Header: "Attested",
            width: "80px",
            filterable: false,
            sortable: false,
            filter: 'fuzzyDate',
            accessor: (pgy: PostGraduateYear) => {
                return (pgy.inEditMode || !pgy.isAttestedComplete) ? (getOverlayAndCheckbox(pgy)) : <div className="text-start">Yes</div>;
            }
        },
        {
            Header: " ",
            width: "130px",
            filterable: false,
            sortable: true,
            accessor: (pgy: PostGraduateYear): any => {
                if (pgy.inEditMode) {
                    return (
                        <div>
                            <button type="button" title="Save" className="btn btn-clean btn-icon btn-icon-md" onClick={() => savePostGraduateYear()}>
                                <i className="la la-save"></i>
                            </button>
                            <a title="Cancel" className="btn btn-clean btn-icon btn-icon-md"
                                onClick={() => cancelEdit()}>
                                <i className="la la-close"></i>
                            </a>
                        </div>
                    );
                }
                return (
                    <div>
                        <label className="btn btn-sm btn-clean btn-icon btn-icon-md mb-0" title={'Upload Document'} hidden={isDocumentUploadButtonHidden(pgy)}>
                            <i className="la la-upload"></i>
                            <input type="file" style={{ display: "none" }} accept="application/pdf" className="form-control" onChange={(event) => addTrainingDocumentToPgy(event, pgy)} />
                        </label>
                        <button type="button" hidden={isPGYEditButtonHidden(pgy)} title="Edit" className="btn btn-sm btn-clean btn-icon btn-icon-md"
                            onClick={() => startEdit(pgy)}>
                            <i className="la la-edit"></i>
                        </button>
                        <button type="button" hidden={isPGYDeleteButtonHidden(pgy)} title="Delete"
                            className="btn btn-sm btn-clean btn-icon btn-icon-md" onClick={() => confirmDeletePostGraduateYear(pgy, true)}>
                            <i className="la la-trash"></i>
                        </button>
                        {getInfoIcon(pgy)}
                    </div>
                );
            }
        }
    ];

    return (
        <>
            <h5 className="fw-bolder ml-2 mt-1 mb-4 text-primary">Training</h5>
            <h6 className="fw-bolder ml-2 mt-1 mb-4 text-secondary">Post Graduate Years&nbsp;
                <Button
                    className="ms-2 pc-button"
                    variant="primary" size="sm"
                    hidden={props.isReadonly || props.isAlumni || (postGraduateYears?.find(_ => _.inEditMode) != null) || (props.programTypeIdsWithApprovedApplication.find(_ => _ === userProgramTypeId) != null)}
                    onClick={() => addPostGraduateYear()}
                    >
                    Add PGY
                </Button>
            </h6>
            {isPsychiatryProgramUser &&
                <Form.Check type="checkbox">
                    <Form.Check.Input defaultChecked={props.isOnFastTrack === true} disabled={props.isAlumni || (props.programTypeIdsWithApprovedApplication.find(_ => _ === TrainingProgramTypes.Psychiatry || _ === TrainingProgramTypes.Cap) != null)} className="pc-checkbox me-2" type="checkbox" onChange={(e) => saveFastTrackStatus(e)} />
                    <Form.Check.Label className="ms-2">
                        <OverlayTrigger overlay={
                            <Tooltip bsPrefix="pc-tooltip">
                            <p><strong>Please select the Child and Adolescent Psychiatry Fellowship Fast Track checkbox if this resident is completing Psychiatry residency and Child and Adolescent Psychiatry fellowship within 5 Post Graduate Years.</strong></p>
                                <p><small>This allows preCERT to accurately display this resident's training requirements and eligibility to apply for an ABPN examination while completing Psychiatry training in the residency program and Child and Adolescent Psychiatry training in the fellowship program.</small></p>
                                <p><small>In the circumstance where the Child and Adolescent Psychiatry program is responsible for the completion of Psychiatry residency training in addition to the Child and Adolescent Psychiatry fellowship training, this selection will allow you to see training requirements and eligibility to apply for both Psychiatry and Child and Adolescent Psychiatry.</small></p>
                                <p><small>You can view the training requirements and eligibility to apply on the right side of this page.</small></p>
                            </Tooltip>}>
                            <div className="mb-2 fw-bold">
                                Child and Adolesent Psychiatry Fellowship Fast Track
                            </div>
                        </OverlayTrigger>
                    </Form.Check.Label>
                </Form.Check>
            }
            {showInvalidUploadAlert &&
                <Alert variant={"danger"} onClose={() => setShowInvalidUploadAlert(false)} dismissible>
                    Invalid Upload. File must be of type PDF and under 5MB in size.
                </Alert>
            }

            {showInvalidAttestationAlert &&
                <Alert variant={"danger"} onClose={() => setShowInvalidAttestationAlert(false)} dismissible>
                    <div>Invalid attestation:</div>
                    <>
                    {attestationValidationErrors.map((error, key) => {
                        return (
                            <Fragment key={key}>
                                <li className="ms-2 mt-1">{error}</li>
                            </Fragment>
                        );
                    })}
                    </>
                </Alert>
            }

            {postGraduateYears?.length > 0 ?
                <>
                    <Modal show={isConfirmingDeletePgy} onHide={() => confirmDeletePostGraduateYear(null, false)}>
                        <Modal.Body>
                            {postGraduateYears.find(_ => _.trainingId === pgyToDelete?.trainingId)?.trainingDocuments?.length > 0 ?
                                'This PGY has documents associated, are you sure you want to delete the PGY and documents?' :
                                'Are you sure you want to delete this PGY?'
                            }
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="primary" onClick={() => deletePostGraduateYear()}>
                                Yes, delete
                            </Button>
                            <Button variant="outline-secondary" onClick={() => confirmDeletePostGraduateYear(null, false)}>
                                No, Cancel
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    <Modal show={isConfirmingDeleteDocument} onHide={() => confirmDeletePostGraduateYear(null, false)}>
                        <Modal.Body>
                            {` Are you sure you want to delete this ${pgyToDelete?.trainingDocuments?.find(_ => _.trainingDocumentId === documentToDelete?.trainingDocumentId)?.documentName} document?`}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="primary" onClick={() => deleteTrainingDocument()}>
                                Yes, delete
                            </Button>
                            <Button variant="outline-secondary" onClick={() => confirmDeleteDocument(null, false)}>
                                No, Cancel
                            </Button>
                        </Modal.Footer>
                    </Modal>
                    <Form id="postGraduateYearForm" ref={editForm} noValidate>
                        <PreCertTable columns={columns} data={postGraduateYears} renderRowSubComponent={({ row }: any) =>
                            renderSubComponent(row)} isRowExpanded={(row: any) => row.original.viewDocuments && (row.original.trainingDocuments != null)}
                            getRowProps={(row: any) => getRowProps(row)} />
                    </Form>
                </>
                :
                <span>No Post Graduate Years</span>
            }
            <div>Total Months/Blocks: {accumalatedTotalAttestedMonths}</div>
        </>
    );
}